import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { formatOrgTime } from "@/shared/utils/date/org-time";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import {
  X,
  Pin,
  PinOff,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  XCircle,
  MapPin,
  Loader2,
} from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Badge } from "@/ui/components/ui/badge";
import { cn } from "@/ui/lib/utils";
import { AlertStatus, type Alert, type AlertImage } from "@/alerts/types/machine-alert";
import { labelConfidencePercent } from "@/shared/utils/confidence";
import AlertImageLightbox from "@/map/components/map-preview/AlertImageLightbox";
import { useMapPinStore } from "@/map/stores/useMapPinStore";
import { availableVerdicts } from "@/alerts/utils/alert-decision";
import type { CloseDispositionRead } from "@/alerts/types/alerts-workspace";

const statusBadge: Record<AlertStatus, string> = {
  [AlertStatus.Pending]: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  [AlertStatus.Resolved]: "bg-green-500/10 text-green-600 border-green-500/20",
  [AlertStatus.Ignored]: "bg-gray-500/10 text-gray-600 border-gray-500/20",
};

/** Wire status → display label. The value on the alert stays the wire value. */
const statusLabelKey: Record<AlertStatus, "pending" | "resolved" | "ignored"> = {
  [AlertStatus.Pending]: "pending",
  [AlertStatus.Resolved]: "resolved",
  [AlertStatus.Ignored]: "ignored",
};

interface MapEventCardProps {
  alert: Alert;
  /**
   * Carousel images (from the alert detail), lifted to the page so the
   * keyboard hook can page them while the lightbox is open. Empty until the
   * detail loads — see `imagesLoading`.
   */
  images: AlertImage[];
  /** Selected image index — owned by the page (single source of truth). */
  imageIndex: number;
  onImageIndexChange: Dispatch<SetStateAction<number>>;
  /** `image_id` of the alert frame (highest-confidence detection), or null. */
  alertFrameId: number | null;
  /** Detail (images) still loading — show a placeholder, not "No image". */
  imagesLoading?: boolean;
  /** Lightbox open state — lifted so arrow/Esc routing can branch on it. */
  lightboxOpen: boolean;
  onLightboxOpenChange: (open: boolean) => void;
  machineName: string;
  index: number;
  total: number;
  /**
   * Whether the open alert holds a slot in the feed. False for one opened from
   * outside it (a timeline segment, or the live rolling window): Prev/Next still
   * step to its neighbours, but "Alert n of N" would be a lie, so it is hidden.
   */
  inFeed?: boolean;
  isUpdating?: boolean;
  /** Shift left of the right-edge so it doesn't overlap the docked video drawer. */
  shiftLeft?: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onResolve: () => void;
  onFalse: () => void;
  /** Canonical result, for offering only the verdict the alert does not carry. */
  currentStatus?: AlertStatus | null;
  currentDisposition?: CloseDispositionRead | null;
  /** The alert moved underneath the operator; shown instead of a silent no-op. */
  conflictMessage?: string | null;
}

/**
 * On-map floating overlay card — the "modal in the map". Mirrors the
 * machine-detail `MapEventOverlay` (collapse / pin / Resolve-False / Prev-Next),
 * driven by a live `Alert`. See docs/map-preview.md.
 */
const MapEventCard = ({
  alert,
  images,
  imageIndex,
  onImageIndexChange,
  alertFrameId,
  imagesLoading = false,
  lightboxOpen,
  onLightboxOpenChange,
  machineName,
  index,
  total,
  inFeed = true,
  isUpdating = false,
  shiftLeft = false,
  onClose,
  onPrev,
  onNext,
  onResolve,
  onFalse,
  currentStatus = null,
  currentDisposition = null,
  conflictMessage = null,
}: MapEventCardProps) => {
  const { t } = useTranslation("map");
  // Empty while canonical detail loads: better to offer nothing than to offer a
  // verdict computed from a possibly stale row (MIN-270).
  const offeredVerdicts = currentStatus
    ? availableVerdicts(currentStatus, currentDisposition)
    : [];
  // Layout lock (persisted, shared with the other map panels): freeze the card's
  // on-map position at pin time so a docking video (`shiftLeft`) or other
  // data/selection change can't move it. The subject still flows — live-follow
  // may swap the alert and its fields keep updating; only the position is frozen.
  const isPinned = useMapPinStore((s) => s.isPinned("alertCard"));
  const togglePin = useMapPinStore((s) => s.togglePin);
  const [frozenShiftLeft, setFrozenShiftLeft] = useState<boolean | null>(null);
  useEffect(() => {
    if (isPinned && frozenShiftLeft == null) setFrozenShiftLeft(shiftLeft);
    else if (!isPinned && frozenShiftLeft != null) setFrozenShiftLeft(null);
  }, [isPinned, shiftLeft, frozenShiftLeft]);
  const effectiveShiftLeft = frozenShiftLeft ?? shiftLeft;

  const [isCollapsed, setIsCollapsed] = useState(false);

  const safeIndex = images.length > 0 ? Math.min(imageIndex, images.length - 1) : 0;
  const currentImage = images[safeIndex] ?? null;
  const isPreviewFrame = currentImage != null && currentImage.image_id === alertFrameId;
  const gps = alert.gps;

  return (
    <div
      className={cn(
        "absolute top-4 z-30 w-80 max-h-[calc(100%-2rem)] flex flex-col rounded-lg border bg-background shadow-2xl animate-in slide-in-from-right-2 duration-200",
        effectiveShiftLeft ? "right-[356px]" : "right-4",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-sidebar/50 rounded-t-lg p-3">
        <h3 className="text-sm font-semibold">{t("alertCard.title")}</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsCollapsed((v) => !v)} title={isCollapsed ? t("alertCard.expand") : t("alertCard.collapse")}>
            {isCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className={cn("h-7 w-7", isPinned && "text-foreground")} onClick={() => togglePin("alertCard")} title={isPinned ? t("alertCard.unpin") : t("alertCard.pin")}>
            {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} title={t("alertCard.close")}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {/* Image carousel — preview (alert frame) first, highlighted.
              Omitted entirely for an alert that has no frames: a black
              sixteen-by-nine box reading "No image" takes the most valuable
              space on the card to say nothing. Still rendered while the detail
              is LOADING, so a slow fetch does not look like an alert without
              evidence. */}
          {(currentImage || imagesLoading) && (
          <div>
            <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
              {currentImage ? (
                <button
                  type="button"
                  onClick={() => onLightboxOpenChange(true)}
                  aria-label={t("alertCard.openFullSize")}
                  className="h-full w-full cursor-zoom-in"
                >
                  <img src={currentImage.signed_url} alt={alert.type} className="h-full w-full object-contain" />
                </button>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-white/60">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}

              {isPreviewFrame && (
                <Badge className="pointer-events-none absolute left-1.5 top-1.5 h-5 px-1.5 text-[10px] bg-amber-500 text-white border-transparent">
                  {t("alertCard.alertFrame")}
                </Badge>
              )}

              {currentImage != null && labelConfidencePercent(currentImage.labels) != null && (
                <div className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                  {t("alertCard.confidence", {
                    value: labelConfidencePercent(currentImage.labels),
                  })}
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label={t("alertCard.previousImage")}
                    onClick={() => onImageIndexChange((i) => (i <= 0 ? images.length - 1 : i - 1))}
                    className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={t("alertCard.nextImage")}
                    onClick={() => onImageIndexChange((i) => (i >= images.length - 1 ? 0 : i + 1))}
                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <div className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                    {safeIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5">
                {images.map((im, i) => {
                  const selected = i === safeIndex;
                  const isPreview = im.image_id === alertFrameId;
                  return (
                    <button
                      key={im.image_id}
                      type="button"
                      onClick={() => onImageIndexChange(i)}
                      title={isPreview ? t("alertCard.alertFrame") : undefined}
                      className={cn(
                        "relative h-10 w-14 shrink-0 overflow-hidden rounded border-2 bg-black",
                        selected ? "border-primary" : isPreview ? "border-amber-500" : "border-transparent",
                      )}
                    >
                      <img src={im.signed_url} alt="" className="h-full w-full object-cover" />
                      {labelConfidencePercent(im.labels) != null && (
                        <span className="absolute right-0 top-0 rounded-bl bg-black/60 px-0.5 text-[7px] leading-3 text-white">
                          {labelConfidencePercent(im.labels)}%
                        </span>
                      )}
                      {isPreview && (
                        <span className="absolute inset-x-0 bottom-0 bg-amber-500/90 text-center text-[7px] font-medium text-white">
                          {t("alertCard.alertBadge")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Date / Time */}
          <div>
            <p className="text-[10px] text-muted-foreground">{t("alertCard.dateTime")}</p>
            <p className="text-xs font-medium font-mono">
              {formatOrgTime(alert.timestamp, "MMM DD, YYYY HH:mm:ss")}
            </p>
          </div>

          {/* Type + origin */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] h-5 px-1.5">{alert.type}</Badge>
            <span className="text-[10px] text-muted-foreground">{alert.origin}</span>
          </div>

          {/* GPS */}
          <div className="flex items-start gap-1.5">
            <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-[10px] text-muted-foreground">{machineName}</p>
              {gps && gps.latitude != null && gps.longitude != null ? (
                <>
                  <p className="text-xs font-mono">
                    {gps.latitude.toFixed(5)}, {gps.longitude.toFixed(5)}
                  </p>
                  {gps.altitude != null && (
                    <p className="text-[10px] text-muted-foreground">
                      {t("alertCard.altitude", {
                        value: formatNumber(gps.altitude, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        }),
                      })}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">{t("alertCard.noGps")}</p>
              )}
            </div>
          </div>

          {/* Status + actions */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-muted-foreground">{t("alertCard.status")}</p>
              <Badge className={cn("text-[10px] h-5 px-1.5", statusBadge[alert.status])}>
                {t(`alertStatus.${statusLabelKey[alert.status]}`)}
              </Badge>
            </div>

            {conflictMessage && (
              <p role="alert" className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                {conflictMessage}
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="default" size="sm" className="flex-1 h-8 text-xs" onClick={onResolve} disabled={isUpdating || !offeredVerdicts.includes("resolved")}>
                <Check className="h-3.5 w-3.5 mr-1" /> {t("alertCard.resolve")}
              </Button>
              <Button variant="destructive" size="sm" className="flex-1 h-8 text-xs" onClick={onFalse} disabled={isUpdating || !offeredVerdicts.includes("false")}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> {t("alertCard.false")}
              </Button>
            </div>

            {total > 1 && (
              <>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={onPrev} disabled={index <= 0}>
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> {t("alertCard.prev")}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={onNext} disabled={index >= total - 1}>
                    {t("alertCard.next")} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
                {inFeed && (
                  <div className="text-center text-[10px] text-muted-foreground">
                    {t("alertCard.position", { index: index + 1, total })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <AlertImageLightbox
        open={lightboxOpen}
        onOpenChange={onLightboxOpenChange}
        images={images}
        index={safeIndex}
        onIndexChange={onImageIndexChange}
        alertFrameId={alertFrameId}
        alt={alert.type}
      />
    </div>
  );
};

export default MapEventCard;
